import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { countryConfig } from '../country';
import { LocaleLink as Link, useLocaleNavigate } from '../i18n/locale-link';
import { LOCALES } from '../i18n/index';
import { stripLocalePrefix } from '../i18n/locale-path';
import { useAuth } from '../lib/auth-context';
import { blurSearchOnSubmit } from '../lib/blur-search-on-submit';
import { usePageTitleValue } from '../lib/page-title';
import { searchTargetPath } from '../lib/search-navigation';
import { useAuthOrigin } from '../lib/auth-origin';
import { useT } from '../i18n/locale-context';
import { userDisplayName, userInitials, isPaidUser } from '../lib/user-display';
import { ProBadge } from './pro-badge';
import { LanguageSwitcher } from './language-switcher';
import { MobileNav } from './mobile-nav';
import { SearchIcon } from './icons';

/**
 * Semantic-search field. Shared between the desktop inline placement (centered
 * in the bar) and the mobile full-width row below the bar — same controlled
 * `query` state, only one is visible at a time (the other is `display:none`, so
 * it's out of the a11y tree and there are no duplicate exposed labels).
 *
 * `text-base` (16px) keeps iOS from zooming the viewport on focus.
 */
function SearchField({
  query,
  onChange,
  onSubmit,
  placeholder,
  aiLabel,
  className,
  dense,
}: {
  query: string;
  onChange: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  placeholder: string;
  aiLabel: string;
  className?: string;
  /** Desktop compact sizing (13.5px). Mobile stays at 16px to avoid iOS zoom. */
  dense?: boolean;
}) {
  return (
    <form onSubmit={onSubmit} className={className}>
      <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
      <input
        type="text"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        /*
         * `truncate` (overflow-hidden + text-overflow: ellipsis + nowrap) —
         * without it the input inherits Tailwind's `text-overflow: clip` and cuts
         * a too-long placeholder MID-WORD with no visual signal that anything is
         * missing. Measured at 390×844: the field offers 247px of text room
         * (355 clientWidth − 40 left − 68 right, the last reserving space for the
         * «AI» badge), and the Uzbek placeholder rendered 269.7px — 22.7px over,
         * so it read «…qidiris». Russian is 214.7px and fits, which is why this
         * only ever showed up in one locale.
         *
         * Kept even though the Uzbek string below is now short enough to fit:
         * 390px is not the narrowest phone in use, `pr-[68px]` is a fixed
         * constant that no locale is measured against, and the next long
         * translation should degrade to an ellipsis rather than to a severed
         * word. The string fixes today's instance; this fixes the class.
         */
        className={`w-full truncate rounded-md border-1.5 border-edge bg-bone pl-10 pr-[68px] text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand focus:bg-bone-surface focus:shadow-[0_0_0_3px_rgb(var(--brand)/0.1)] ${
          dense ? 'h-[38px] text-[13.5px]' : 'h-11 text-base'
        }`}
      />
      <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 rounded bg-brand-light px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em] text-brand">
        {aiLabel}
      </span>
    </form>
  );
}

/** m16 top bar: brand wordmark, semantic search with AI badge, language + account. */
export function Topbar() {
  const t = useT();
  const navigate = useLocaleNavigate();
  const authOrigin = useAuthOrigin();
  const [searchParams] = useSearchParams();
  const { status, user } = useAuth();
  const [query, setQuery] = useState('');

  // Keep the input in sync with the URL's ?q= (e.g. after navigation).
  useEffect(() => {
    setQuery(searchParams.get('q') ?? '');
  }, [searchParams]);

  // /results has its own search — the «Что вы поставляете?» card (owner,
  // 2026-10-07) — so the semantic box is not drawn there at all: two search
  // fields on one page, one of which leaves the archive, is the confusion the
  // card replaced. Path-based, so the prerender and the first client render
  // agree.
  const barePath = stripLocalePrefix(useLocation().pathname);
  const showSearch = !(barePath === '/results' || barePath.startsWith('/results/'));

  const isAuthed = status === 'authed' && user;
  const initials = isAuthed ? userInitials(user) : null;
  const pageTitle = usePageTitleValue();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Phones: the keyboard's «search» key must also close the keyboard, or it
    // hides the results it just asked for (see the helper).
    blurSearchOnSubmit(e.currentTarget);
    void navigate(searchTargetPath(query, searchParams));
  }

  const placeholder = t('shell.search.placeholder');
  const aiLabel = t('shell.search.ai');

  return (
    <header className="shrink-0 border-b border-edge bg-bone-surface">
      {/* Row 1 — nav + brand + account. On lg it's the whole bar. */}
      <div className="flex h-14 items-center gap-3 px-4 sm:px-6 lg:h-[60px] lg:gap-4">
        {/* Hamburger — mobile/tablet only (button carries its own lg:hidden) */}
        <MobileNav />

        {/* Contextual page title next to the hamburger. When a page declares a
            title the bar reads it (the body H1 goes sr-only, reclaiming the title
            row); on pages with no title (landing) it's the "UzTender.com" wordmark that
            links home. The visual title is aria-hidden — the sr-only body H1
            carries the heading semantics, so screen readers hear it once. */}
        {pageTitle ? (
          <span
            aria-hidden="true"
            // Hidden below `sm`: between the hamburger, the language switch and
            // «Создать аккаунт» a 360–393 px bar leaves ~50 px, and the title
            // rendered as «Кат…» / «Тен…» / «Рын…» (measured 2026-09-30, Pixel 5).
            // Three letters and an ellipsis tell the reader nothing; the sr-only
            // body H1 still carries the heading.
            className="hidden min-w-0 truncate font-brand text-[17px] font-extrabold tracking-[-0.3px] text-ink sm:block"
          >
            {pageTitle}
          </span>
        ) : (
          <Link to="/" className="font-brand text-[17px] font-extrabold tracking-[-0.3px] text-ink">
            {countryConfig.brand.name}
          </Link>
        )}

        {/* Semantic search — desktop inline (centered) */}
        {showSearch && (
          <SearchField
            query={query}
            onChange={setQuery}
            onSubmit={handleSubmit}
            placeholder={placeholder}
            aiLabel={aiLabel}
            dense
            className="relative mx-auto hidden w-full max-w-[520px] lg:block"
          />
        )}

        {/* Right cluster */}
        <div
          className={`ml-auto flex shrink-0 items-center gap-2 lg:gap-3 ${showSearch ? 'lg:ml-0' : ''}`}
        >
          {LOCALES.length > 1 && <LanguageSwitcher />}
          {isAuthed ? (
            <Link
              to="/app"
              aria-label={userDisplayName(user)}
              className="relative flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:opacity-85"
            >
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt=""
                  className="h-10 w-10 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-brand to-[#0d9090] text-[11.5px] font-bold text-white">
                  {initials}
                </span>
              )}
              {isPaidUser(user) && <ProBadge size={15} />}
            </Link>
          ) : (
            // Guests: register is the primary action (not-yet-registered), with
            // login as the secondary text link for returning users. Below `sm`
            // the row is too tight for both long labels (esp. uz "Ro‘yxatdan
            // o‘tish") next to the language switcher, so the primary CTA clipped
            // off-screen — hide the secondary login there (it's still one tap
            // away in the hamburger drawer) and keep the register CTA whole.
            //
            // …except from 390 px, where a compact «Войти» (px-2) fits beside
            // the register CTA: measured 2026-10-16 on the live header (webfont,
            // both sites, ru/kk/uz), at 393 px «Создать аккаунт» keeps 14 px to
            // the screen edge. At 360 px (the Redmi from the kaztender.com phone
            // pass) the Russian pair pushes «Создать аккаунт» 11 px off-screen
            // even with every gap tightened, so there it stays in the drawer.
            // Only where the bar shows a page TITLE (hidden below `sm`): a page
            // without one shows the «KazTender.com» wordmark instead, and there
            // the register CTA alone already ran 54 px off a 393 px screen
            // (/about, /offer — same measurement).
            <div className="flex items-center gap-1.5">
              {/* `state={authOrigin}` — the page they are leaving, so signing in
                  from a tender returns them to that tender and not to the
                  catalogue (or, worse, to /plans with a sheet opening itself).
                  See `useAuthOrigin` / `postAuthPath`. */}
              <Link
                to="/login"
                state={authOrigin}
                className={`hidden h-[34px] items-center whitespace-nowrap rounded-md px-2 text-[13px] font-semibold text-ink-soft transition-colors hover:bg-bone hover:text-ink sm:inline-flex sm:px-3 ${
                  pageTitle ? 'min-[390px]:inline-flex' : ''
                }`}
              >
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                state={authOrigin}
                className="inline-flex h-[34px] items-center whitespace-nowrap rounded-md bg-brand px-3 text-[13px] font-semibold text-white transition-colors hover:bg-brand-mid"
              >
                {t('login.signUp')}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Row 2 — full-width semantic search on mobile/tablet only */}
      {showSearch && (
        <div className="px-4 pb-3 sm:px-6 lg:hidden">
          <SearchField
            query={query}
            onChange={setQuery}
            onSubmit={handleSubmit}
            placeholder={placeholder}
            aiLabel={aiLabel}
            className="relative w-full"
          />
        </div>
      )}
    </header>
  );
}
